import { ClaudeTool, Command, CommandId, SelfContainedCommand } from 'types/commands';
import { CommandHandler } from 'types/commands';
import { create } from 'zustand';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('COMMAND_REGISTRY', { enabled: false });

import { commands as docsCommands } from '../apps/DocsApp/store/commands';
import { commands as pdfiumCommands } from '../apps/PDFiumApp/commands';
import { listenAppCommands } from '../apps/ListenApp/commands';
import { libraryCommands } from '../apps/LibraryApp/commands';
import {
  spaceLifecycleCommands,
  windowManagementCommands,
  panelVisibilityCommands,
  sidebarTreeCommands,
  workflowUtilityCommands,
  driveNavigationCommands,
} from '../systemCommands/spaceUI';
import { billingCommands } from '../systemCommands/billing/billingCommands';
import { entityCommands } from '../systemCommands/entitySystem';
import { browserCommands } from '../apps/BrowserApp/commands';
import { userInteractionCommands } from '../systemCommands/userInteraction';
import { UNIVERSAL_COMMANDS } from '../apps/UniversalCommands'; // ✅ NEW: Universal v2 entity commands
import { writersDashboardCreateCommand } from '../apps/WritersDashboardApp_v2/commands/writers_dashboard_create';
import { dashboardCreateCommand } from '../apps/DashboardApp/commands/dashboard_create';
import { manuscriptReviewReportCreateCommand } from '../apps/ManuscriptReviewReportApp_v2/commands/manuscript_review_report_create';
import { bookProjectCreateCommand } from '../apps/BookFormatterApp_v2/commands/book_project_create';
import { bookWriteCommand } from '../apps/BookFormatterApp_v2/commands/book_write';

import { commands as _bankCommandsImport } from '../apps/BankApp/store/commands';
const bankCommands: any[] = _bankCommandsImport || [];

// Import red flag workflow commands
import { registerV5Commands } from '../apps/DocsApp/commands/v5';
import { aiCommands } from './aiCommands';
import { tasksCommands } from '../apps/TasksApp/store/commands';
import { characterCommands } from '../commands/character';
import { notesCommands } from '../apps/NotesMarkdownApp/commands';
import { DATABASE_COMMANDS as databaseCommands } from '../apps/DatabaseApp/commands';
import {
  VIDEO_STUDIO_PROJECT_COMMANDS,
  VIDEO_STUDIO_COMMANDS,
} from '../apps/VideoStudioApp/commands';
import { SHEETS_COMMANDS } from '../apps/SheetsApp_v2/commands';
import { PRESENTATIONS_COMMANDS } from '../apps/PresentationsApp_v2/commands';
import { googleDriveCommands } from '../apps/GoogleDriveApp/commands';
import { wordPressCommands } from '../apps/WordPressApp/commands';
import { toolCommands } from '../systemCommands/execution/local';
import { newWindowCommands } from '../apps/NewWindowApp/commands';
import { openDialogCommands } from '../commands/openDialog';
import { canvasCommands } from '../apps/CanvasApp/commands';
import { apiConnectCommand, publicApiQueryCommand, enableAPIConnectionCommand, disableAPIConnectionCommand, apiConnectionReadCommand } from '../systemCommands/integrations';
import { apiConnectionCommands } from '../apps/APIConnectionApp/commands';
import { computeSessionCommand } from '../systemCommands/execution/cloud/computeSession';
import { mailAppCommands } from '../apps/MailApp/commands';
import { AUTOMATIONS_COMMANDS } from '../apps/AutomationsApp/commands/v1';
import { planCreateCommand } from '../apps/PlanApp/commands/v1';
import { todoWriteCommand, todoReadCommand, planUpdateCommand } from '../systemCommands/agentTools/todo_write';
import { pageCommands } from '../apps/PageApp_v2/commands/v1';
import { contactsCommands } from '../apps/ContactsApp/commands/v1';
import { appBuilderCommands } from '../apps/AppBuilderApp/commands';
import { rulesCommands } from '../apps/RulesApp_v2/commands';
import { promptsCommands } from '../apps/PromptsApp_v2/commands';
import { memoryCommands } from '../apps/MemoryApp_v2/commands';
import { markdownDocCommands } from '../apps/MarkdownApp_v2/commands';
import { inboxCommands } from '../apps/InboxApp/commands';
import { shopCommands } from '../commands/shop';
import { passwordVaultCommands } from '../apps/PasswordVaultApp/commands';
import { DOC_SYSTEM_COMMANDS } from '../systemCommands/docSystem';
import { CANVAS_SYSTEM_COMMANDS } from '../systemCommands/canvasSystem';
import { IMAGE_STUDIO_COMMANDS } from '../apps/MediaViewerApp/commands/imageStudioCommands';
import { registerMediaCommandsInRegistry } from '../services/mediaCommandService';


// Define the command registry state
interface CommandRegistryState {
  commands: Map<CommandId, CommandHandler>;
  commandDefinitions: Map<CommandId, Command | SelfContainedCommand>;
}

// Define the command registry store
interface CommandRegistryStore extends CommandRegistryState {
  registerCommand: (id: CommandId, handler: CommandHandler) => void;
  unregisterCommand: (id: CommandId) => void;
  executeCommand: <T>(id: CommandId, params: any, context?: any, onProgress?: (progress: any) => void) => Promise<T | undefined>;
  registerCommandDefinition: (command: Command | SelfContainedCommand) => void;
  getCommandDefinition: (id: CommandId) => Command | SelfContainedCommand | undefined;
}

// Create the command registry store
export const useCommandRegistryStore = create<CommandRegistryStore>((set, get) => ({
  commands: new Map(),
  commandDefinitions: new Map(),

  registerCommand: (id: CommandId, handler: CommandHandler) => {
    set((state) => {
      const newCommands = new Map(state.commands);
      newCommands.set(id, handler);
      return { commands: newCommands };
    });
  },

  unregisterCommand: (id: CommandId) => {
    set((state) => {
      const newCommands = new Map(state.commands);
      newCommands.delete(id);
      return { commands: newCommands };
    });
  },

  executeCommand: async <T>(id: CommandId, params: any, context?: any, onProgress?: (progress: any) => void): Promise<T | undefined> => {
    let { commands, commandDefinitions } = get();

    // Primary lookup in the handler cache; fall back to commandDefinitions if the cache is stale
    let handler = commands.get(id) ?? (commandDefinitions.get(id) as any)?.handler;

    // If not found, re-initialize the full registry and retry.
    // This handles partial registry states (e.g. only tool commands registered via HMR/lazy-load,
    // but initializeAllCommands never ran for this module instance).
    if (!handler) {
      const log = createFileLogger('COMMAND_REGISTRY');
      log.warn(`[CommandRegistry] Command "${id}" not found (registry has ${commandDefinitions.size} commands). Re-initializing...`);
      initializeAllCommands();
      ({ commands, commandDefinitions } = get());
      handler = commands.get(id) ?? (commandDefinitions.get(id) as any)?.handler;
    }
    
    if (handler) {
      try {
        // Build the execution context, including the onProgress callback
        const executionContext = {
          ...(context || {}),
          onProgress
        };

        let result;
        
        // New standard: handler(params, context)
        // Self-contained commands and new legacy commands follow this
        if (handler.length >= 2) {
          result = await handler(params, executionContext);
        } else {
          // Legacy: handler({ ...params, context })
          const legacyParams = {
            ...params,
            context: executionContext
          };
          result = await handler(legacyParams);
        }

        return result;
      } catch (error) {
        log.error(`Error executing command ${id}:`, error);
        
        // Re-throw the error so it can be caught by the CommandExecutor
        throw error;
      }
    } else {
      log.warn(`Command ${id} not found`);
      
      // Throw an error instead of returning undefined
      throw new Error(`Command not found: ${id}`);
    }
  },

  registerCommandDefinition: (command: Command | SelfContainedCommand) => {
    // Attach a default formatResultPreview if missing, to ensure completed cards show a meaningful label
    const enhanced = { ...command } as any;
    if (typeof enhanced.formatResultPreview !== 'function') {
      enhanced.formatResultPreview = (result: any, toolInput: any) => {
        try {
          // Prefer StandardToolResult.message
          if (result && typeof result.message === 'string' && result.message.length > 0) {
            return result.message;
          }
          // Prefer explicit summaries on data
          if (result && result.data) {
            const data = result.data;
            if (typeof data.summary === 'string' && data.summary.length > 0) return data.summary;
            // Generic search-style summaries
            const arrays = [
              ['results', Array.isArray((data as any).results) ? (data as any).results.length : undefined],
              ['hits', Array.isArray((data as any).hits) ? (data as any).hits.length : undefined],
              ['nodes', Array.isArray((data as any).nodes) ? (data as any).nodes.length : undefined],
              ['items', Array.isArray((data as any).items) ? (data as any).items.length : undefined],
              ['matches', Array.isArray((data as any).matches) ? (data as any).matches.length : undefined],
            ].filter(([_, v]) => typeof v === 'number' && (v as number) >= 0) as Array<[string, number]>;
            if (arrays.length > 0) {
              const [label, count] = arrays[0];
              return `Found ${count} ${label}`;
            }
            if (typeof (data as any).total === 'number') return `Found ${(data as any).total} results`;
            if (typeof (data as any).count === 'number') return `Found ${(data as any).count} results`;
            // ✅ ENHANCED: Use enhanced message context if available
            if (data.messageContext) {
              const ctx = data.messageContext;
              if (typeof ctx.operationSummary === 'string') {
                const preview = ctx.contentPreview ? ` - ${ctx.contentPreview}` : '';
                return `${ctx.operationSummary}${preview}`;
              }
            }
            // Common fields across doc tools
            if (typeof data.replacedCount === 'number') return `Replaced ${data.replacedCount} node(s)`;
            if (typeof data.removedCount === 'number') return `Removed ${data.removedCount} node(s)`;
            if (Array.isArray(data.insertedNodeIds)) return `Inserted ${data.insertedNodeIds.length} node(s)`;
            if (typeof data.type === 'string') return `Completed ${data.type.replace(/^document_/, '').replace(/_/g, ' ')}`;
          }
          // Fallback to input-provided expand text
          if (toolInput && typeof toolInput.expand === 'string' && toolInput.expand.length > 0) {
            return toolInput.expand;
          }
        } catch {}
        return 'Completed';
      };
    }
    set((state) => {
      const newDefinitions = new Map(state.commandDefinitions);
      newDefinitions.set(enhanced.id, enhanced);
      return { commandDefinitions: newDefinitions };
    });
  },

  getCommandDefinition: (id: CommandId) => {
    const { commandDefinitions } = get();
    return commandDefinitions.get(id);
  },
}));

// Helper function to execute commands with type safety
// export const executeCommand = async <T>(id: CommandId, ...args: any[]): Promise<T | undefined> => {
//   return useCommandRegistryStore.getState().executeCommand<T>(id, ...args);
// };


export const createToolsFromCommands = (commands: (Command | SelfContainedCommand)[]): ClaudeTool[] => {
  return commands.map(command => {
    // Handle both Command and SelfContainedCommand types
    const parameters = 'parameters' in command 
      ? command.parameters // Legacy Command
      : command.schema.parameters; // SelfContainedCommand

    return {
      name: command.id,
      description: command.description,
      input_schema: {
        type: "object",
        properties: Object.fromEntries(
          parameters.map((param: any) => {
            const propertyDef: any = {
              type: param.type,
              description: param.description
            };
            
            // Include default value if present (helps Claude understand optional parameters)
            if (param.defaultValue !== undefined) {
              propertyDef.default = param.defaultValue;
            }
            
            // For array types, include items schema if present (critical for Claude to understand array structure)
            if (param.type === 'array' && param.items) {
              propertyDef.items = param.items;
            }
            
            return [param.name, propertyDef];
          })
        ),
        required: parameters
          .filter((param: any) => param.required)
          .map((param: any) => param.name)
      }
    };
  });
};

export const registerCommands = (commands: (SelfContainedCommand | Command)[]) => {
  const commandRegistry = useCommandRegistryStore.getState();
  commands.forEach(command => {
    commandRegistry.registerCommand(command.id, command.handler);
    // Store the command directly - registry now supports both types
    commandRegistry.registerCommandDefinition(command);
  });
};

// NEW: Register self-contained commands
export const registerSelfContainedCommands = (commands: any[]) => {
  const commandRegistry = useCommandRegistryStore.getState();
  const registeredIds: string[] = [];
  commands.forEach(command => {
    // Self-contained commands have a different handler signature but the registry doesn't care
    commandRegistry.registerCommand(command.id, command.handler);
    commandRegistry.registerCommandDefinition(command);
    registeredIds.push(command.id);
  });
  
};

// Initialize all app commands
export const initializeAllCommands = () => {
  log.info('[CommandRegistry] Starting command initialization...');
  
  // Clear existing commands via set() so Zustand state is consistent
  useCommandRegistryStore.setState({
    commands: new Map(),
    commandDefinitions: new Map(),
  });
  
  // Register all space/window/panel/tree commands from system/spaceUI/
  registerSelfContainedCommands(spaceLifecycleCommands);
  registerSelfContainedCommands(windowManagementCommands);
  registerSelfContainedCommands(panelVisibilityCommands);
  registerSelfContainedCommands(sidebarTreeCommands);
  registerSelfContainedCommands(workflowUtilityCommands);
  registerSelfContainedCommands(driveNavigationCommands);
  registerSelfContainedCommands(billingCommands);
  
  // Register unified entity commands (includes CRUD + filesystem operations like folder management)
  registerSelfContainedCommands(entityCommands);
  
  // Get docs commands dynamically (this will now always include Y.js commands)
  const activeDocsCommands = docsCommands;
  log.info('[CommandRegistry] Got active docs commands:', {
    count: activeDocsCommands.length,
    ids: activeDocsCommands.map(c => c.id),
    yjsCount: activeDocsCommands.filter(c => c.id.startsWith('yjs_')).length
  });
  
  registerCommands(activeDocsCommands);

  // Register the new V5 markdown commands
  registerV5Commands();
  
  registerCommands(pdfiumCommands);
  registerCommands(bankCommands);
  registerCommands(browserCommands);
  
  // Register user interaction commands (for agentic workflows)
  registerSelfContainedCommands(userInteractionCommands);
  
  // Register AI commands as self-contained
  registerSelfContainedCommands(aiCommands);
  
  // Register Tasks app commands
  registerSelfContainedCommands(tasksCommands);

  // Register Password Vault commands
  registerSelfContainedCommands(passwordVaultCommands);
  
  // Register Character commands
  registerSelfContainedCommands(characterCommands);
  
  // Register Notes commands
  registerSelfContainedCommands(notesCommands);

  // Register DatabaseApp (Table / TableRow) commands
  registerSelfContainedCommands(databaseCommands);

  // Register VideoStudioApp — project creation + thin video_* tool wrappers.
  // Progress / result broadcast handlers register below alongside IMAGE_STUDIO_COMMANDS
  // since they follow the same global-broadcast pattern.
  registerSelfContainedCommands(VIDEO_STUDIO_PROJECT_COMMANDS);
  registerSelfContainedCommands(VIDEO_STUDIO_COMMANDS);
  
  // Register Sheets commands
  registerSelfContainedCommands(SHEETS_COMMANDS);
  
  // Register Presentations commands
  registerSelfContainedCommands(PRESENTATIONS_COMMANDS);
  
  // Register Dashboard (analytics dashboard) create command
  registerSelfContainedCommands([dashboardCreateCommand]);

  // Register Writers Dashboard commands
  registerSelfContainedCommands([writersDashboardCreateCommand]);

  // Register Manuscript Review Report commands
  registerSelfContainedCommands([manuscriptReviewReportCreateCommand]);

  // Register Book Formatter commands
  registerSelfContainedCommands([bookProjectCreateCommand, bookWriteCommand]);
  
  // Register Listen app commands (text-to-speech)
  registerSelfContainedCommands(listenAppCommands);
  
  // Register Library app commands (reference management)
  registerSelfContainedCommands(libraryCommands);
  
  // Register Mail app commands
  registerSelfContainedCommands(mailAppCommands);
  
  // Register Automations commands (automation_list, automation_create, automation_run, automation_enable, automation_disable)
  registerSelfContainedCommands(AUTOMATIONS_COMMANDS);

  // Register Skill and Plan commands
  registerSelfContainedCommands([planCreateCommand]);

  // Register in-chat task plan commands
  registerSelfContainedCommands([todoWriteCommand, todoReadCommand, planUpdateCommand]);

  // Register Contacts app-specific commands (enrich + discover)
  // contact_create, contact_find, contact_update live in UNIVERSAL_COMMANDS
  registerSelfContainedCommands(contactsCommands);

  // Register Page app commands
  registerSelfContainedCommands(pageCommands);
  
  // Register Google Drive commands
  registerSelfContainedCommands(googleDriveCommands);
  
  // Register WordPress commands
  registerSelfContainedCommands(wordPressCommands);
  
  // Register Tool Execution commands
  registerSelfContainedCommands(toolCommands);
  
  // Register API Connection commands
  registerSelfContainedCommands([
    apiConnectCommand,
    publicApiQueryCommand,
    apiConnectionReadCommand,
    enableAPIConnectionCommand,
    disableAPIConnectionCommand
  ]);
  registerSelfContainedCommands(apiConnectionCommands);
  log.info('[CommandRegistry] ✅ API Connection commands registered:', {
    systemCommands: ['api_connect', 'public_api_query', 'api_connection_read', 'enable_api_connection', 'disable_api_connection'],
    appCommands: apiConnectionCommands.map(c => c.id)
  });
  
  // Register Compute Session command
  registerSelfContainedCommands([computeSessionCommand]);
  log.info('[CommandRegistry] ✅ Compute Session command registered:', {
    computeSession: 'compute_session'
  });
  
  // Register NewWindow commands
  registerSelfContainedCommands(newWindowCommands);
  
  // Register OpenDialog commands
  registerSelfContainedCommands(openDialogCommands);
  // Register Canvas commands
  registerSelfContainedCommands(canvasCommands);
  
  // Register App Builder commands (Phase 3)
  registerSelfContainedCommands(appBuilderCommands);
  
  // Register Shop commands (for e-commerce products)
  registerSelfContainedCommands(shopCommands);
  log.info('[CommandRegistry] ✅ App Builder commands registered:', {
    count: appBuilderCommands.length,
    ids: appBuilderCommands.map(c => c.id)
  });
  
  // Register Rules commands (rule_create)
  registerSelfContainedCommands(rulesCommands);

  // Register Prompts / Memory / MarkdownDoc create commands
  registerSelfContainedCommands(promptsCommands);
  registerSelfContainedCommands(memoryCommands);
  registerSelfContainedCommands(markdownDocCommands);

  // Register InboxApp commands (inboxapp_create, inbox_item_create)
  registerSelfContainedCommands(inboxCommands);

  // Register Doc System commands (undo, redo, download, TOC, comments, help chat)
  registerSelfContainedCommands(DOC_SYSTEM_COMMANDS);

  // Register Canvas System commands (download / frame export)
  registerSelfContainedCommands(CANVAS_SYSTEM_COMMANDS);

  // Register Image Studio agent-broadcast handlers (image_studio_progress / _result).
  // These must be global so an agent-side tool can deliver results even before
  // any ImageStudio window is mounted, and must NOT be re-registered per-mount.
  registerSelfContainedCommands(IMAGE_STUDIO_COMMANDS);

  // ✅ NEW: Register Universal Commands (v2 entity commands)
  registerSelfContainedCommands(UNIVERSAL_COMMANDS);
  log.info('[CommandRegistry] ✅ Registered Universal Commands:', {
    count: UNIVERSAL_COMMANDS.length,
    ids: UNIVERSAL_COMMANDS.map(c => c.id)
  });

  // Register media commands (media_speak, window_action_poll).
  // Must be inside initializeAllCommands so they survive any re-initialization
  // of the full command map (previously they were mount-time-only and would
  // disappear if the registry was re-initialized after Space.tsx unmounted).
  registerMediaCommandsInRegistry(useCommandRegistryStore.getState());
  
  log.info('[CommandRegistry] ✅ Initialized all commands:', {
    spaceLifecycle: spaceLifecycleCommands.length,
    windowManagement: windowManagementCommands.length,
    panelVisibility: panelVisibilityCommands.length,
    sidebarTree: sidebarTreeCommands.length,
    workflowUtility: workflowUtilityCommands.length,
    entity: entityCommands.length,
    docs: activeDocsCommands.length,
    pdfium: pdfiumCommands.length,
    bank: bankCommands.length,
    browser: browserCommands.length,
    userInteraction: userInteractionCommands.length,
    ai: aiCommands.length,
    universal: UNIVERSAL_COMMANDS.length,
    entityCommandIds: entityCommands.map(c => c.id),
    browserCommandIds: browserCommands.map(c => c.id),
    userInteractionCommandIds: userInteractionCommands.map(c => c.id),
    docsCommandIds: activeDocsCommands.map(c => c.id),
    universalCommandIds: UNIVERSAL_COMMANDS.map(c => c.id),
    yjsCommandsFound: activeDocsCommands.filter(c => c.id.startsWith('yjs_')).length,
    yjsCommandIds: activeDocsCommands.filter(c => c.id.startsWith('yjs_')).map(c => c.id)
  });
};

/**
 * Registry-First Pattern: Get commands for specific contexts
 * This is the SINGLE source of truth for all command access
 */
export const getCommandsForContext = (context: 'api' | 'tools' | 'ui' | 'all' = 'all'): (Command | SelfContainedCommand)[] => {
  const registry = useCommandRegistryStore.getState();
  const allCommands = Array.from(registry.commandDefinitions.values());
  
  // Context-specific filtering
  switch (context) {
    case 'api':
      // For backend API - ensure no duplicates, include all essential commands
      return allCommands.filter(() => {
        // Include all commands but ensure docs commands are properly filtered
        return true;
      });
      
    case 'tools':
      // For tool discovery - include commands that should be exposed to AI
      return allCommands.filter(cmd => {
        // Exclude internal/debug commands if needed
        return !cmd.id.includes('debug') && !cmd.id.includes('internal');
      });
      
    case 'ui':
      // For UI contexts - include commands for contextual tool selection
      return allCommands;
      
    case 'all':
    default:
      return allCommands;
  }
};

/**
 * Get commands by app type for contextual filtering
 */
export const getCommandsByApp = (appType: string): (Command | SelfContainedCommand)[] => {
  const allCommands = getCommandsForContext('all');
  
  // Filter commands by app prefix or app-specific logic
  const filtered = allCommands.filter(cmd => {
    if (appType === 'docs' || appType === 'doc') {
      return cmd.id.startsWith('docs_') || cmd.id.startsWith('yjs_') || cmd.id.startsWith('doc_');
    }
    if (appType === 'notes' || appType === 'note') {
      return cmd.id.startsWith('notes_');
    }
    if (appType === 'pdf' || appType === 'pdfium') {
      return cmd.id.startsWith('pdf_') || cmd.id.startsWith('pdfium_');
    }
    if (appType === 'bank') {
      return cmd.id.startsWith('bank_');
    }
    if (appType === 'google-drive') {
      return cmd.id.startsWith('google_');
    }
    if (appType === 'tasks') {
      return cmd.id.startsWith('task_') || cmd.id.startsWith('tasks_');
    }
    if (appType === 'book') {
      return cmd.id.startsWith('book_') || cmd.id === 'entity_read';
    }
    // ✅ UNIVERSAL: Commands that work with ANY v2 entity
    if (appType === 'universal') {
      return cmd.id.startsWith('entity_');
    }
    return false;
  });
  
  if (appType === 'docs' || appType === 'doc') {
    log.debug('[CommandRegistry][getCommandsByApp] Docs commands filtered:', {
      appType,
      totalCommands: allCommands.length,
      docsCommands: filtered.length,
      docsCommandIds: filtered.map(c => c.id),
      yjsCommands: filtered.filter(c => c.id.startsWith('yjs_')).length,
      legacyCommands: filtered.filter(c => c.id.startsWith('docs_')).length,
      commands4: filtered.filter(c => c.id.startsWith('doc_')).length
    });
  }
  
  if (appType === 'google-drive') {
    log.log('[google tools feature] Google Drive commands filtered:', {
      appType,
      totalCommands: allCommands.length,
      googleDriveCommands: filtered.length,
      commandIds: filtered.map(c => c.id)
    });
  }
  
  return filtered;
};

/**
 * Ensure commands are initialized before accessing them
 */
export const ensureCommandsInitialized = (): void => {
  const registry = useCommandRegistryStore.getState();
  if (registry.commandDefinitions.size === 0) {
    log.warn('[CommandRegistry] Commands not initialized, initializing now...');
    initializeAllCommands();
  }
};

/** Convert JSON Schema properties to the SelfContainedCommand parameter format. */
function buildParametersFromSchema(inputSchema: Record<string, any>): any[] {
  const properties = inputSchema?.properties ?? {};
  const required: string[] = inputSchema?.required ?? [];

  return Object.entries(properties).map(([name, def]: [string, any]) => ({
    name,
    type: def.type ?? 'string',
    description: def.description ?? '',
    required: required.includes(name),
    enum: def.enum,
    defaultValue: def.default,
  }));
}

/**
 * Register commands from installed App Bundles.
 * Called asynchronously after initializeAllCommands() — non-fatal if it fails.
 * Bundle commands delegate to callToolRuntime() just like first-party commands.
 * Built-in commands always win name conflicts.
 */
export const registerBundleCommands = (bundles: Array<{ bundleId: string; bundleName: string; tools: Array<{ name: string; description: string; inputSchema: Record<string, any>; handlerUrl: string }> }>): void => {
  const registry = useCommandRegistryStore.getState();
  let registered = 0;
  let skipped = 0;

  for (const bundle of bundles) {
    for (const tool of bundle.tools) {
      // Built-in wins on conflict
      if (registry.commands.has(tool.name as any)) {
        log.warn(`[CommandRegistry] ⚠️ bundle command "${tool.name}" conflicts with built-in — skipping`);
        skipped++;
        continue;
      }

      const command: any = {
        id: tool.name,
        description: tool.description,
        schema: {
          parameters: buildParametersFromSchema(tool.inputSchema),
        },
        handler: async (params: any, context: any) => {
          const { callToolRuntime } = await import('../utils/callToolRuntime');
          return callToolRuntime(tool.name, params, context);
        },
      };

      registry.registerCommand(tool.name as any, command.handler);
      registry.registerCommandDefinition(command);
      registered++;
    }
  }

  if (registered > 0 || skipped > 0) {
    log.info(`[CommandRegistry] registered ${registered} bundle commands from ${bundles.length} installed bundles`, {
      skipped,
    });
  }
};
